"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "@/src/lib/next-compat";
import { acceptInvitationApiV1InvitationsAcceptPost } from "@/src/api/sdk.gen";
import { useUser, useClerk, SignUp, SignIn } from "@clerk/clerk-react";
import { apiFetch } from "@/src/lib/api-fetch";
import { WorkspaceCourseSelector, type JoinWorkspacePreview } from "@/src/components/WorkspaceCourseSelector";

interface InviteDetails {
    valid: boolean;
    email?: string | null;
    organization_name?: string | null;
    organization_slug?: string | null;
    role?: string | null;
    status?: string | null;
    invitation_type?: string | null;
    workspace_id?: number | null;
    workspace_name?: string | null;
    workspaces: JoinWorkspacePreview[];
}

function AcceptInvitationContent() {
    const { user, isLoaded } = useUser();
    const { signOut } = useClerk();
    const navigate = useNavigate();
    const searchParams = useSearchParams();
    const rawToken = searchParams.get("token") || "";
    const token =
        rawToken ||
        (typeof window !== "undefined" ? sessionStorage.getItem("inara_invite_token") || "" : "");

    useEffect(() => {
        if (rawToken && typeof window !== "undefined") {
            sessionStorage.setItem("inara_invite_token", rawToken);
        }
    }, [rawToken]);

    const [status, setStatus] = useState<"loading" | "success" | "no-invitations" | "error" | "auth-required" | "choose-workspace">("loading");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [orgNames, setOrgNames] = useState<string[]>([]);
    const [targetRedirect, setTargetRedirect] = useState<string>("/courses");
    const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
    const [inviteDetailsLoaded, setInviteDetailsLoaded] = useState(false);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);
    const [acceptRequested, setAcceptRequested] = useState(false);
    const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");
    const hasRun = useRef(false);

    // Fetch invitation details if user is not yet signed in
    useEffect(() => {
        if (!token) {
            setStatus("error");
            setInviteDetailsLoaded(true);
            return;
        }

        async function fetchInviteDetails() {
            try {
                const res = await apiFetch(`/api/v1/invitations/details?token=${encodeURIComponent(token)}`);
                if (res.ok) {
                    const data = await res.json();
                    const details = data as InviteDetails;
                    setInviteDetails(details);
                    const workspaces = details.workspaces || [];
                    setSelectedWorkspaceId(
                        workspaces.some((workspace) => workspace.id === details.workspace_id)
                            ? details.workspace_id ?? null
                            : workspaces[0]?.id ?? null,
                    );
                    if (data.organization_slug && data.role === "admin") {
                        setTargetRedirect(`/org/${data.organization_slug}/dashboard`);
                    } else {
                        setTargetRedirect("/courses");
                    }
                } else {
                    setStatus("error");
                }
            } catch (err) {
                console.error("Failed to load invitation info:", err);
                setStatus("error");
            } finally {
                setInviteDetailsLoaded(true);
            }
        }

        fetchInviteDetails();
    }, [token]);

    // Handle invitation acceptance once user is authenticated
    useEffect(() => {
        if (!isLoaded) return;

        if (!user) {
            setStatus("auth-required");
            return;
        }

        if (!inviteDetailsLoaded) return;

        if (token && !inviteDetails?.valid && inviteDetails?.status !== "accepted") {
            setStatus("error");
            return;
        }

        // Strict email matching: prevent accepting with a different Google / Clerk account
        const currentEmail = user.primaryEmailAddress?.emailAddress?.trim().toLowerCase();
        const invitedEmail = inviteDetails?.email?.trim().toLowerCase();
        if (currentEmail && invitedEmail && currentEmail !== invitedEmail) {
            setErrorMessage(
                `This invitation was sent to ${inviteDetails.email}, but you are signed in as ${user.primaryEmailAddress?.emailAddress}. Please switch accounts to accept this invitation.`
            );
            setStatus("error");
            return;
        }

        const canChooseWorkspace =
            inviteDetails?.status !== "accepted" &&
            inviteDetails?.invitation_type !== "org_admin_activation" &&
            !inviteDetails?.workspace_id &&
            (inviteDetails?.workspaces?.length || 0) > 1;
        if (canChooseWorkspace && !acceptRequested) {
            setStatus("choose-workspace");
            return;
        }

        // Guard against React strict mode double-firing
        if (hasRun.current) return;
        hasRun.current = true;

        let redirectTimeout: NodeJS.Timeout | null = null;

        async function acceptInvitations() {
            setStatus("loading");
            try {
                const { data, error } = await acceptInvitationApiV1InvitationsAcceptPost({
                    body: {
                        token: token || "",
                        workspace_id: selectedWorkspaceId ?? undefined,
                    },
                });

                const resData = (data || error) as any;

                if (!resData) {
                    setStatus("error");
                    return;
                }

                // Handle known error cases
                const errCode = resData?.error || resData?.detail?.error;
                const errDetail =
                    (typeof resData?.detail === "string" ? resData.detail : resData?.detail?.detail || resData?.detail?.message) ||
                    resData?.message ||
                    resData?.error;

                if (errCode === "email_mismatch" || resData.error === "email_mismatch") {
                    setErrorMessage(
                        errDetail ||
                        `This invitation was sent to ${resData.invited_email || inviteDetails?.email || "the invited email"}, but you are signed in as ${user.primaryEmailAddress?.emailAddress || "a different account"}. Please switch accounts to accept this invitation.`
                    );
                    setStatus("error");
                    return;
                }
                if (errCode === "invalid_token" || resData.error === "invalid_token") {
                    setErrorMessage("This invitation token is invalid or has expired.");
                    setStatus("error");
                    return;
                }

                if (error) {
                    const errObj = error as { detail?: string | { detail?: string; message?: string }; message?: string } | undefined;
                    const msg =
                        typeof errObj?.detail === "string"
                            ? errObj.detail
                            : errObj?.detail?.detail || errObj?.detail?.message || errObj?.message || "Failed to process invitation.";
                    setErrorMessage(msg);
                    setStatus("error");
                    return;
                }

                const dest =
                    resData.redirect_url ||
                    (resData.role === "admin" && resData.organization_slug
                        ? `/org/${resData.organization_slug}/dashboard`
                        : "/courses");

                setTargetRedirect(dest);

                if (typeof window !== "undefined") {
                    sessionStorage.removeItem("inara_invite_token");
                }

                if (resData.accepted === 0) {
                    setStatus("no-invitations");
                    redirectTimeout = setTimeout(() => navigate(dest), 1800);
                    return;
                }

                setOrgNames(resData.organizations || (resData.organization_name ? [resData.organization_name] : []));
                setStatus("success");
                redirectTimeout = setTimeout(() => navigate(dest), 1800);
            } catch {
                setStatus("error");
            }
        }

        acceptInvitations();

        return () => {
            if (redirectTimeout) clearTimeout(redirectTimeout);
        };
    }, [isLoaded, user, token, signOut, navigate, inviteDetails, inviteDetailsLoaded, selectedWorkspaceId, acceptRequested]);

    // If user is not authenticated, render the branded registration / set-password view
    if (!user && status === "auth-required") {
        const returnUrl = `/invitations/accept?token=${encodeURIComponent(token)}`;

        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
                <div className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-200 text-center mb-6">
                    <div className="w-12 h-12 bg-indigo-50 border border-indigo-200 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 font-black text-xl">
                        ✦
                    </div>
                    <h1 className="text-xl font-extrabold text-slate-900">
                        {inviteDetails?.invitation_type === "org_admin_activation"
                            ? `Activate ${inviteDetails?.organization_name || "Enterprise"} Workspace`
                            : inviteDetails?.organization_name
                            ? `Join ${inviteDetails.organization_name}`
                            : "Organization Invitation"}
                    </h1>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                        {inviteDetails?.email ? (
                            <>
                                {inviteDetails.invitation_type === "org_admin_activation" ? (
                                    <>
                                        An activation email was sent to <strong className="text-slate-800">{inviteDetails.email}</strong>.
                                        Set up your administrator password below to activate and access your enterprise workspace.
                                    </>
                                ) : (
                                    <>
                                        An invitation was sent to <strong className="text-slate-800">{inviteDetails.email}</strong>.
                                        Set up your password below to access your team&apos;s workspace.
                                    </>
                                )}
                            </>
                        ) : (
                            "Please set up your password or sign in to accept your invitation."
                        )}
                    </p>

                    {/* Switch between Register / Sign In */}
                    <div className="flex bg-slate-100 p-1 rounded-xl mt-5 mb-2">
                        <button
                            type="button"
                            onClick={() => setAuthMode("signup")}
                            className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${
                                authMode === "signup"
                                    ? "bg-white text-indigo-700 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            {inviteDetails?.invitation_type === "org_admin_activation"
                                ? "Set Password & Activate"
                                : "Set Password & Register"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setAuthMode("signin")}
                            className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${
                                authMode === "signin"
                                    ? "bg-white text-indigo-700 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            Already have an account?
                        </button>
                    </div>
                </div>

                <div className="w-full max-w-md flex justify-center">
                    {authMode === "signup" ? (
                        <SignUp
                            routing="hash"
                            initialValues={
                                inviteDetails?.email ? { emailAddress: inviteDetails.email } : undefined
                            }
                            fallbackRedirectUrl={returnUrl}
                            forceRedirectUrl={returnUrl}
                            signInUrl={`/invitations/accept?token=${encodeURIComponent(token)}`}
                        />
                    ) : (
                        <SignIn
                            routing="hash"
                            initialValues={
                                inviteDetails?.email ? { emailAddress: inviteDetails.email } : undefined
                            }
                            fallbackRedirectUrl={returnUrl}
                            forceRedirectUrl={returnUrl}
                            signUpUrl={`/invitations/accept?token=${encodeURIComponent(token)}`}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 max-w-md w-full text-center">
                {status === "loading" && (
                    <>
                        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                        <h2 className="text-lg font-bold text-slate-900">Setting up your team access...</h2>
                        <p className="text-xs text-slate-500 mt-2">Connecting your account to your organization workspace.</p>
                    </>
                )}

                {status === "success" && (
                    <>
                        <div className="w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-4 text-emerald-600 font-bold text-2xl">
                            ✓
                        </div>
                        <h2 className="text-xl font-black text-slate-900">Welcome to the Team!</h2>
                        <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                            You&apos;ve successfully activated and joined <strong className="text-slate-900">{orgNames.join(", ") || inviteDetails?.organization_name || "the organization"}</strong>.
                            Taking you to your workspace dashboard...
                        </p>
                        <button
                            onClick={() => navigate(targetRedirect)}
                            className="mt-4 inline-flex items-center gap-1.5 bg-indigo-600 text-white rounded-xl px-5 py-2.5 text-xs font-bold hover:bg-indigo-700 transition shadow-sm"
                        >
                            Open Workspace Dashboard →
                        </button>
                    </>
                )}

                {status === "no-invitations" && (
                    <>
                        <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-600 font-bold text-xl">
                            ✓
                        </div>
                        <h2 className="text-lg font-bold text-slate-900">Already Active</h2>
                        <p className="text-xs text-slate-500 mt-2">You are already enrolled in this workspace. Redirecting to your dashboard...</p>
                        <button
                            onClick={() => navigate(targetRedirect)}
                            className="mt-4 inline-flex items-center gap-1.5 bg-indigo-600 text-white rounded-xl px-5 py-2.5 text-xs font-bold hover:bg-indigo-700 transition shadow-sm"
                        >
                            Go to Dashboard →
                        </button>
                    </>
                )}

                {status === "error" && (
                    <>
                        <div className="w-12 h-12 bg-rose-50 border border-rose-200 rounded-2xl flex items-center justify-center mx-auto mb-4 text-rose-600 font-bold text-xl">
                            ✕
                        </div>
                        <h2 className="text-lg font-bold text-slate-900">Invitation Unavailable</h2>
                        <p className="text-xs text-slate-500 mt-2">{errorMessage || "This invitation may have expired or is invalid. Please contact your organization administrator."}</p>
                        <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
                            <button
                                onClick={() => navigate("/courses")}
                                className="bg-indigo-600 text-white rounded-xl px-5 py-2.5 font-bold hover:bg-indigo-700 transition shadow-sm text-xs cursor-pointer"
                            >
                                Go to Dashboard
                            </button>
                            <button
                                onClick={() => signOut(() => navigate(`/invitations/accept?token=${encodeURIComponent(token)}`))}
                                className="border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl px-4 py-2.5 font-bold transition text-xs cursor-pointer"
                            >
                                Switch Account
                            </button>
                        </div>
                    </>
                )}

                {status === "choose-workspace" && (
                    <div className="text-left space-y-4">
                        <div className="text-center">
                            <h2 className="text-lg font-bold text-slate-900">Select Your Workspace</h2>
                            <p className="text-xs text-slate-500 mt-1">
                                Choose the workspace in <strong className="text-slate-900">{inviteDetails?.organization_name || "your organization"}</strong> that you belong to:
                            </p>
                        </div>

                        <WorkspaceCourseSelector
                            workspaces={inviteDetails?.workspaces || []}
                            selectedWorkspaceId={selectedWorkspaceId}
                            onSelect={(wsId: number) => setSelectedWorkspaceId(wsId)}
                        />

                        <button
                            type="button"
                            onClick={() => {
                                setAcceptRequested(true);
                                hasRun.current = false;
                            }}
                            className="w-full mt-2 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs transition shadow-sm cursor-pointer"
                        >
                            Join Workspace &amp; Continue →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AcceptInvitationPage() {
    return (
        <Suspense
            fallback={
                <div className="min-h-screen flex items-center justify-center bg-slate-50">
                    <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 max-w-md w-full text-center">
                        <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                        <h2 className="text-lg font-bold text-slate-900">Loading invitation...</h2>
                    </div>
                </div>
            }
        >
            <AcceptInvitationContent />
        </Suspense>
    );
}
